import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import axios from "axios";
import API_ENDPOINTS from "../config/api";

// 쿠키 설정 가져오기
const { KEYS: COOKIE_KEYS } = API_ENDPOINTS.COOKIE_CONFIG;

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 50,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: "tween",
      ease: "easeInOut",
      duration: 0.6,
    },
  },
  exit: {
    opacity: 0,
    y: -50,
    transition: {
      duration: 0.4,
    },
  },
};

function SignatureAgreementPage() {
  const navigate = useNavigate();
  const pageRef = useRef(null);
  const topRef = useRef(null);
  const canvasRef = useRef(null);
  const signatureSectionRef = useRef(null);
  const [userInfo, setUserInfo] = useState(null);

  const [agreements, setAgreements] = useState({
    signatureAgreement: false,
  });

  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [signatureError, setSignatureError] = useState("");
  const [hasStartedDrawing, setHasStartedDrawing] = useState(false);

  // 쿠키에서 사용자 정보 가져오기
  useEffect(() => {
    const userInfoFromCookie = Cookies.get(COOKIE_KEYS.USER_INFO);
    if (userInfoFromCookie) {
      try {
        const parsedUserInfo = JSON.parse(userInfoFromCookie);
        setUserInfo(parsedUserInfo);
      } catch (error) {}
    }
  }, []);

  // 날인 동의 및 서명 확인하여 버튼 활성화 상태 업데이트
  useEffect(() => {
    setIsButtonEnabled(
      agreements.signatureAgreement && signatureData && !signatureError
    );
  }, [agreements.signatureAgreement, signatureData, signatureError]);

  // 페이지를 벗어날 때 스크롤 초기화
  useEffect(() => {
    return () => {
      // 컴포넌트 언마운트 시 스크롤을 최상단으로 초기화
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
  }, []);

  // Canvas 초기화
  useEffect(() => {
    if (canvasRef.current && agreements.signatureAgreement) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = 4; // 해상도가 높아졌으므로 선 두께도 증가
      ctx.lineCap = "round";

      // 플레이스홀더 이미지 그리기
      if (!hasStartedDrawing) {
        const img = new Image();
        img.onload = () => {
          const scale = 0.6; // 이미지 크기를 60%로 축소
          const scaledWidth = img.width * scale;
          const scaledHeight = img.height * scale;
          const x = (canvas.width - scaledWidth) / 2;
          const y = (canvas.height - scaledHeight) / 2;
          ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
        };
        img.src = "/sign.png";
      }
    }
  }, [agreements.signatureAgreement, hasStartedDrawing]);

  // 마우스/터치 이벤트 핸들러
  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // 첫 번째 그리기 시작 시 플레이스홀더 텍스트 지우기
    if (!hasStartedDrawing) {
      setHasStartedDrawing(true);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = 4; // 해상도가 높아졌으므로 선 두께도 증가
      ctx.lineCap = "round";
    }

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    // 서명 데이터 저장
    if (canvasRef.current) {
      const dataURL = canvasRef.current.toDataURL();
      setSignatureData(dataURL);

      // 서명 길이 체크
      checkSignatureLength();
    }
  };

  // 서명 길이 체크 함수
  const checkSignatureLength = () => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // 서명이 그려진 픽셀 수 계산
      let signaturePixels = 0;
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] > 0) {
          // 알파값이 0보다 큰 픽셀 (서명이 그려진 부분)
          signaturePixels++;
        }
      }

      // 서명이 너무 짧으면 에러 메시지 표시
      if (signaturePixels < 500) {
        // 임계값 조정 가능
        setSignatureError("서명이 너무 짧습니다. 더 길게 작성해주세요.");
      } else {
        setSignatureError("");
      }
    }
  };

  // 서명 지우기
  const clearSignature = () => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureData(null);
      setSignatureError(""); // 에러 메시지도 지우기
      setHasStartedDrawing(false); // 플레이스홀더 다시 표시하기 위해 리셋
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = 4; // 해상도가 높아졌으므로 선 두께도 증가
      ctx.lineCap = "round";
      // 플레이스홀더 이미지 다시 그리기
      const img = new Image();
      img.onload = () => {
        const scale = 0.6; // 이미지 크기를 60%로 축소
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        const x = (canvas.width - scaledWidth) / 2;
        const y = (canvas.height - scaledHeight) / 2;
        ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
      };
      img.src = "/sign.png";
    }
  };

  // Presigned URL 가져오기 함수
  const getPresignedUrls = async (files) => {
    try {
      const response = await axios.get(
        `${API_ENDPOINTS.BASE_URL}/mobile/file/upload`,
        {
          params: {
            type: "image",
            mimeType: "image/png",
            imageUploadTarget: "signatureCardImage",
            num: files.length,
          },
          headers: {
            Authorization: `Bearer ${Cookies.get(COOKIE_KEYS.ACCESS_TOKEN)}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // 서명 이미지 업로드 함수
  const uploadSignatureImage = async () => {
    if (!signatureData) {
      throw new Error("서명이 없습니다.");
    }

    try {
      // 1. Base64 이미지를 File 객체로 변환
      const imageFile = await (async () => {
        const response = await fetch(signatureData);
        const blob = await response.blob();
        return new File([blob], `signature_${Date.now()}.png`, {
          type: "image/png",
        });
      })();

      // 2. Presigned URL 가져오기
      const presignedUrlData = await getPresignedUrls([imageFile]);

      if (!presignedUrlData || !presignedUrlData[0]?.url) {
        throw new Error("Presigned URL을 가져오는데 실패했습니다.");
      }

      // 3. S3에 업로드
      const presignedUrl = presignedUrlData[0].url;
      const s3Response = await fetch(presignedUrl, {
        method: "PUT",
        body: imageFile,
        headers: {
          "Content-Type": "image/png",
        },
      });

      if (!s3Response.ok) {
        throw new Error(
          `S3 업로드 실패: ${s3Response.status} ${s3Response.statusText}`
        );
      }

      // S3 URL 추출 및 이미지 경로만 사용
      const originalUrl = presignedUrl.split("?")[0];

      // 현재 날짜 기반으로 경로 생성 (YYYYMM)
      const date = new Date();
      const yearMonth = `${date.getFullYear()}${String(
        date.getMonth() + 1
      ).padStart(2, "0")}`;

      // 파일명 추출
      const fileName = originalUrl.split("/").pop();

      // 백엔드 요청 형식에 맞게 경로만 전송
      const imagePath = `images/origin/signatureCardImage/${yearMonth}/${fileName}`;

      // 4. 서명 이미지 URL을 서버에 전송
      const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim/signatureCardImage`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            signatureCardImage: imagePath,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`서명 이미지 업로드 실패: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // 날인 동의 처리 함수
  const handleSignatureAgreement = () => {
    const newAgreementState = !agreements.signatureAgreement;
    setAgreements((prev) => ({
      ...prev,
      signatureAgreement: newAgreementState,
    }));

    // 체크박스가 체크되면 서명 섹션으로 부드럽게 스크롤
    if (newAgreementState) {
      setTimeout(() => {
        if (signatureSectionRef.current) {
          signatureSectionRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 1000);
    } else {
      // 체크박스가 해제되면 서명 상태 초기화
      setSignatureData(null);
      setSignatureError("");
      setHasStartedDrawing(false);
    }
  };

  // 다음 버튼 클릭 처리
  const handleNextButtonClick = async () => {
    if (!isButtonEnabled) return;

    try {
      // 서명 이미지 업로드
      await uploadSignatureImage();

      // 서명 동의 API 호출
      const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
      const signatureAgreeResponse = await fetch(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim/signatureAgree`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!signatureAgreeResponse.ok) {
        throw new Error(
          `서명 동의 API 호출 실패: ${signatureAgreeResponse.status}`
        );
      }

      // 스크롤을 최상단으로 초기화
      if (topRef.current) {
        topRef.current.scrollIntoView({
          behavior: "auto",
          block: "start",
        });
      }

      // 약간의 지연 후 다음 페이지로 이동
      setTimeout(() => {
        navigate("/id-card-submit");
      }, 100);
    } catch (error) {
      console.error("다음 버튼 클릭 오류:", error);
      alert("서명 업로드에 실패했습니다. 다시 시도해주세요.");
    }
  };

  return (
    <>
      <motion.div
        className="page-container"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        ref={pageRef}
      >
        {/* 최상단 ref 요소 */}
        <div
          ref={topRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "1px",
            height: "1px",
          }}
        />

        <motion.div
          className="page-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          style={{
            marginTop: "10px",
          }}
        >
          <h1>위임장 날인 동의</h1>
        </motion.div>

        <motion.div
          className="agreement-content"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2
            style={{
              width: "100%",
              textAlign: "left",
              paddingLeft: "10px",
              marginTop: "20px",
              marginBottom: "0px",
            }}
          >
            위임장 날인 동의
          </h2>
          <p
            style={{
              textAlign: "left",
              paddingLeft: "10px",
              lineHeight: "1.4",
            }}
          >
            귀하의 성함으로 위임장에 날인하는 것에 <br /> 대한 동의를
            요청드립니다.
          </p>

          <div className="agreement-items">
            <motion.div
              className="agreement-item"
              onClick={handleSignatureAgreement}
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              whileHover={{ scale: 1.01 }}
              style={{
                backgroundColor: agreements.signatureAgreement
                  ? "#F9F8FF"
                  : "#ffffff",
                border: agreements.signatureAgreement
                  ? "1px solid #7A4FFF"
                  : "1px solid #E5E7EB",
              }}
            >
              <div className="checkbox-image">
                <img
                  src={
                    agreements.signatureAgreement
                      ? "/checked.png"
                      : "/checkedInActive.png"
                  }
                  alt="체크박스"
                />
              </div>
              <label>
                위임장 날인 임의 생성 동의
                <span className="required">[필수]</span>
              </label>
            </motion.div>

            {/* 서명 Canvas */}
            {agreements.signatureAgreement && (
              <motion.div
                ref={signatureSectionRef}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                style={{
                  marginTop: "20px",
                  padding: "20px",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 15px 0",
                    fontSize: "16px",
                    fontWeight: "800",
                    color: "#374151",
                  }}
                >
                  날인 동의를 위한 서명을 작성해주세요
                </h3>
                <img
                  src="/signatureWarring.png"
                  alt="서명 경고"
                  style={{
                    width: "300px",
                    height: "auto",
                    marginBottom: "15px",
                    display: "block",
                  }}
                />
                <div
                  style={{
                    position: "relative",
                    border: "2px solid #E5E7EB",
                    borderRadius: "8px",
                    backgroundColor: "#ffffff",
                    overflow: "hidden",
                  }}
                >
                  <canvas
                    ref={canvasRef}
                    width={600}
                    height={360}
                    style={{
                      display: "block",
                      cursor: "crosshair",
                      touchAction: "none",
                      width: "300px",
                      height: "180px",
                    }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      const touch = e.touches[0];
                      const mouseEvent = new MouseEvent("mousedown", {
                        clientX: touch.clientX,
                        clientY: touch.clientY,
                      });
                      startDrawing(mouseEvent);
                    }}
                    onTouchMove={(e) => {
                      e.preventDefault();
                      const touch = e.touches[0];
                      const mouseEvent = new MouseEvent("mousemove", {
                        clientX: touch.clientX,
                        clientY: touch.clientY,
                      });
                      draw(mouseEvent);
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      stopDrawing();
                    }}
                  />
                </div>
                <div
                  style={{
                    marginTop: "15px",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={clearSignature}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#7A4FFF",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    서명 재작성
                  </button>
                </div>
              </motion.div>
            )}
            {/* 서명 에러 메시지 */}
            {agreements.signatureAgreement && signatureError && (
              <div
                style={{
                  color: "#DC4B3F",
                  fontSize: "14px",
                  marginTop: "0px",
                  textAlign: "left",
                }}
              >
                {signatureError}
              </div>
            )}
          </div>
        </motion.div>

        {/* 하단 고정 버튼을 위한 여백 */}
        <div style={{ height: "100px" }}></div>
      </motion.div>

      {/* 하단 고정 버튼 */}
      <motion.button
        className="confirm-button"
        disabled={!isButtonEnabled}
        onClick={handleNextButtonClick}
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
        whileHover={isButtonEnabled ? { scale: 1.02 } : {}}
        whileTap={isButtonEnabled ? { scale: 0.98 } : {}}
        ref={pageRef}
        style={{
          position: "fixed",
          bottom: "20px",
          left: "5%",
          transform: "translateX(-50%)",
          width: "90%",
          maxWidth: "460px",
          height: "56px",
          fontSize: "16px",
          fontWeight: "600",
          background: isButtonEnabled ? "#7A4FFF" : "#EBEBFF",
          color: isButtonEnabled ? "#EBEBFF" : "#fff",
          border: "none",
          borderRadius: "8px",
          cursor: isButtonEnabled ? "pointer" : "not-allowed",
          transition: "background-color 0.2s ease",
          boxShadow: isButtonEnabled
            ? "0 2px 5px rgba(0, 117, 255, 0.2)"
            : "none",
          zIndex: 1000,
        }}
      >
        다음
      </motion.button>
    </>
  );
}

export default SignatureAgreementPage;
